import type { UserDocumentType } from '@rio/types'
import * as React from 'react'
import { useDispatch } from 'react-redux'

import { trackInternalEvent } from '@rio/tracking'
import { useSearchParams } from 'react-router-dom'
import {
  CareerInsightsPaywallModal,
  CareerInsightsStatusModal,
} from 'builder/components/CareerInsights'
import CioLimitModal from 'builder/components/CioLimitModal'
import CoRegModal from 'builder/components/CoRegistration/Modal'
import CoverLetterModal from 'builder/components/CoverLetterModal'
import DocxModal from 'builder/components/DocxModal'
import MergeDocumentsModal from 'builder/components/MergeDocumentsModal'
import PrefillResumeModal from 'builder/components/PrefillModal'
import RateModal from 'builder/components/RateModal'
import ShareModal from 'builder/components/ShareModal'
import SnackBar from 'builder/components/SnackBar'
import TREditModal from 'builder/components/TREditModal'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { useI18n } from 'builder/hooks/useI18n'
import { useQueryParam } from 'builder/hooks/useQueryParam'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as careerProfileSelectors } from 'builder/modules/careerProfile'
import { PanelSessionStorageKeys, ResumeEditorQueryParams } from 'builder/modules/panel'
import { actions as editorActions } from 'builder/modules/resumeEditor'
import { actions as uiActions, selectors as uiSelectors } from 'builder/modules/ui'
import { selectors as dashboardSelectors } from 'builder/modules/dashboard'
import tunerURL from 'images/modals/resume.jpg'
import { RestartSubscriptionModal } from '../RestartSubscriptionModal'

const MagicContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch()
  const [limitDocumentType] = useQueryParam<UserDocumentType>('limit_document_type')
  const coverLetterModalId = useTypedSelector(uiSelectors.coverLetterModalId)
  const docxModalDocumentId = useTypedSelector(uiSelectors.docxModalDocumentId)
  const docxModalDocumentType = useTypedSelector(uiSelectors.docxModalDocumentType)
  const isRateModalOpen = useTypedSelector(uiSelectors.isRateModalOpen)
  const showRestartSubscriptionModal = useTypedSelector(uiSelectors.showRestartSubscriptionModal)
  const isMergeModalOpen = useTypedSelector(uiSelectors.isMergeModalOpen)
  const prefillModalDocumentId = useTypedSelector(uiSelectors.prefillModalDocumentId)
  const limitModalDocumentType = useTypedSelector(uiSelectors.limitModalType)
  const careerInsightsStatusModalType = useTypedSelector(
    careerProfileSelectors.careerInsightsStatusModalType,
  )
  const careerInsightsPaywallModalType = useTypedSelector(
    careerProfileSelectors.openedPaywallModalType,
  )
  const isTREditModalOpen = useTypedSelector(store => store.ui.isTREditModalOpen)
  const editModalTrDocId = useTypedSelector(store => store.ui.editModalTrDocId)
  const showUpgradeModal = useTypedSelector(dashboardSelectors.showUpgradeModal)
  const [query] = useSearchParams()
  const showDocxModal = docxModalDocumentType && docxModalDocumentId
  const { i18n } = useI18n()

  useEffectOnMount(() => {
    if (limitDocumentType) {
      dispatch(uiActions.openLimitModal(limitDocumentType))
    }
    if (query.has(ResumeEditorQueryParams.isResumeTailor)) {
      dispatch(editorActions.setOpenOptimizerModal(true))
      dispatch(editorActions.startResumeOptimizerProcess())
    }
  })

  const handleCancel = () => {
    dispatch(uiActions.closeLimitModal())
    sessionStorage.removeItem(PanelSessionStorageKeys.ACTION_AFTER_UPGRADING_PLAN)
    limitModalDocumentType && trackInternalEvent(`close_${limitModalDocumentType}_limit_modal`)
  }
  return (
    <React.Fragment>
      {children}
      <SnackBar />
      <ShareModal />
      {showDocxModal && (
        <DocxModal documentId={docxModalDocumentId} documentType={docxModalDocumentType} />
      )}
      <CoRegModal />
      {isRateModalOpen && <RateModal />}
      {isMergeModalOpen && <MergeDocumentsModal />}
      {coverLetterModalId && <CoverLetterModal letterId={coverLetterModalId} />}
      {prefillModalDocumentId && <PrefillResumeModal documentId={prefillModalDocumentId} />}
      {limitModalDocumentType && !showUpgradeModal && (
        <CioLimitModal
          documentType={limitModalDocumentType}
          cancelButton={i18n.t('builder.job_tracking.limit_modal.cancel')}
          upgradebutton={i18n.t('builder.job_tracking.limit_modal.upgrade')}
          tunerImg={tunerURL}
          onClose={() => handleCancel()}
        />
      )}
      {isTREditModalOpen && editModalTrDocId && <TREditModal id={editModalTrDocId} />}
      {careerInsightsStatusModalType && (
        <CareerInsightsStatusModal type={careerInsightsStatusModalType} />
      )}
      {careerInsightsPaywallModalType && (
        <CareerInsightsPaywallModal type={careerInsightsPaywallModalType} />
      )}
      {showRestartSubscriptionModal && <RestartSubscriptionModal />}
    </React.Fragment>
  )
}

export default MagicContainer
